import axios from 'axios';

// const auth = JSON.parse(localStorage.getItem('machadalo-credentials'));
// const service = axios.create({
//     baseURL: process.env.REACT_APP_API_URL,
//     headers: {
//         'Content-Type': 'application/json',
//         'Authorization': `JWT ${auth?.token}`
//     },
// });
const CONSISTENT_KEY_B64 = process.env.REACT_APP_API_ENCRYPTION_KEY;
const ENCRYPTION_ENABLED = process.env.REACT_APP_ENABLE_API_ENCRYPTION === 'true';
const base64ToUint8Array = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

let cachedKey = null;

const getConsistentKey = async () => {
  if (!ENCRYPTION_ENABLED) return null;
  if (cachedKey) return cachedKey;

  const keyBuffer = base64ToUint8Array(CONSISTENT_KEY_B64);
  cachedKey = await crypto.subtle.importKey('raw', keyBuffer, 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
  return cachedKey;
};

// export const encryptPayload = async (data) => {
//   if (!ENCRYPTION_ENABLED) return data;
//   const key = await getConsistentKey();
//   const iv = crypto.getRandomValues(new Uint8Array(12));
//   const encoded = new TextEncoder().encode(JSON.stringify(data));

//   const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

//   const combined = new Uint8Array(iv.length + encrypted.byteLength);
//   combined.set(iv, 0);
//   combined.set(new Uint8Array(encrypted), iv.length);

//   return btoa(String.fromCharCode(...combined));
// };

export const encryptPayload = async (data) => {
  if (!ENCRYPTION_ENABLED) return data;
  const key = await getConsistentKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));

  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);

  let binary = '';
  for (let i = 0; i < combined.length; i++) {
    binary += String.fromCharCode(combined[i]);
  }
  return btoa(binary);
};

export const decryptPayload = async (encryptedB64) => {
  if (!ENCRYPTION_ENABLED) return encryptedB64;
  const key = await getConsistentKey();
  const encrypted = base64ToUint8Array(encryptedB64);

  const iv = encrypted.slice(0, 12);
  const ciphertext = encrypted.slice(12);

  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);

  return JSON.parse(new TextDecoder().decode(decrypted));
};

const service = axios.create({
  // baseURL: 'https://devapi.machadalo.com',
  baseURL: process.env.REACT_APP_API_URL,
});

service.interceptors.request.use(
  async (config) => {
    // Attach JWT
    const credentialsString = localStorage.getItem('machadalo-credentials');
    if (credentialsString) {
      try {
        const credentials = JSON.parse(credentialsString);
        if (credentials?.token) {
          config.headers['Authorization'] = `JWT ${credentials.token}`;
        }
      } catch (e) {
        console.error('JWT parse error', e);
      }
    }

    // Skip encryption for:
    // - multipart/form-data
    // - blob downloads
    // - empty body
    const contentType = config.headers?.['Content-Type'];
    const isMultipart = contentType?.includes('multipart/form-data');
    const isBlob = config.responseType === 'blob';
    if (!ENCRYPTION_ENABLED) return config;
    if (config.data && !isMultipart && !isBlob) {
      const encryptedData = await encryptPayload(config.data);
      config.data = { encrypted_data: encryptedData };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

service.interceptors.response.use(
  async (response) => {
    // Skip blob responses
    if (!ENCRYPTION_ENABLED) {
      if (response?.status === 403) {
        localStorage.clear();
        return (window.location.href = '/r/session-expired');
      }
      return response;
    }
    if (response.config.responseType === 'blob') {
      return response;
    }

    // Decrypt encrypted responses
    if (response?.data?.encrypted_data) {
      const decrypted = await decryptPayload(response.data.encrypted_data);
      response.data = decrypted;
    }
    if (response?.status === 403) {
      localStorage.clear();
      return (window.location.href = '/r/session-expired');
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 403) {
      localStorage.clear();
      window.location.href = '/r/session-expired';
    }
    if (error?.response?.data?.encrypted_data) {
      const decrypted = await decryptPayload(error?.response?.data?.encrypted_data);
      error.response.data = decrypted;
    }
    return Promise.reject(error);
  }
);
async function get(path) {
  const response = await service.get(path);
  return {
    status: response.status,
    data: response?.data?.data || response?.data,
    count: response?.data?.total_supplier || response?.data?.total || response?.data?.count || 0,
  };
}

async function post(path, payload, headers = { 'Content-Type': 'application/json' }) {
  return service.post(path, payload, { headers });
}

async function put(path, payload, headers = { 'Content-Type': 'application/json' }) {
  return service.put(path, payload, { headers });
}

async function patch(path, payload, headers = { 'Content-Type': 'application/json' }) {
  return service.patch(path, payload, { headers });
}

async function remove(path, payload) {
  return service.delete(path, { data: payload });
}

async function uploadFile(path, payload, onUploadProgress) {
  return service.post(path, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
}

async function downloadFile(path, defaultFileName = 'download.xlsx') {
  const response = await service.get(path, { responseType: 'blob' });

  const contentDisposition = response.headers['content-disposition'];
  let fileName = defaultFileName;

  if (contentDisposition?.includes('filename=')) {
    fileName = contentDisposition.split('filename=')[1].replace(/"/g, '');
  }

  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();

  return true;
}

async function downloadFilePOST(path, formData, defaultFileName = 'download.xlsx') {
  const response = await service.post(path, formData, { responseType: 'blob' });

  const contentDisposition = response.headers['content-disposition'];
  let fileName = defaultFileName;

  if (contentDisposition?.includes('filename=')) {
    fileName = contentDisposition.split('filename=')[1].replace(/"/g, '');
  }

  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();

  return true;
}

export default {
  get,
  post,
  put,
  patch,
  remove,
  uploadFile,
  downloadFile,
  downloadFilePOST,
};

// service.interceptors.request.use(
//   (config) => {
//     const credentialsString = localStorage.getItem('machadalo-credentials');
//     if (credentialsString) {
//       try {
//         const credentials = JSON.parse(credentialsString);
//         if (credentials && credentials?.token) {
//           config.headers['Authorization'] = `JWT ${credentials.token}`;
//         }
//       } catch (e) {
//         console.error('Failed to parse credentials from localStorage for API request', e);
//       }
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );
// export default service;

// async function get(path) {
//   try {
//     const response = await service.get(path);
//     return {
//       status: response.status,
//       data: response?.data?.data || response?.data,
//       count: response?.data?.total_supplier || response?.data?.total || response?.data?.count || 0,
//     };
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return error;
//   }
// }

// async function post(path, payload, headers = { 'content-type': 'application/json' }) {
//   try {
//     const response = await service.request({
//       method: 'POST',
//       url: path,
//       responseType: 'json',
//       data: payload,
//       headers: headers,
//     });
//     return response;
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return { status: error?.response?.status, data: error?.response?.data };
//   }
// }

// async function put(path, payload, headers = { 'content-type': 'application/json' }) {
//   try {
//     const response = await service.request({
//       method: 'PUT',
//       url: path,
//       responseType: 'json',
//       data: payload,
//       headers: headers,
//     });
//     return { status: response.status, data: response.data };
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return { status: error?.response?.status, data: error?.response?.data };
//   }
// }

// async function patch(path, payload, headers = { 'content-type': 'application/json' }) {
//   try {
//     const response = await service.request({
//       method: 'PATCH',
//       url: path,
//       responseType: 'json',
//       data: payload,
//       headers: headers,
//     });
//     return { status: response.status, data: response.data };
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return { status: error?.response?.status, data: error?.response?.data };
//   }
// }

// async function remove(path, payload) {
//   try {
//     const response = await service.request({
//       method: 'DELETE',
//       url: path,
//       responseType: 'json',
//       data: payload,
//     });
//     return response;
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return error;
//   }
// }

// async function uploadFile(
//   path,
//   payload,
//   onUploadProgress,
//   headers = {
//     'Content-Type': 'multipart/form-data',
//   }
// ) {
//   try {
//     const response = await service.request({
//       method: 'POST',
//       url: path,
//       responseType: 'json',
//       data: payload,
//       headers: headers,
//       onUploadProgress: onUploadProgress,
//     });
//     return response;
//   } catch (error) {
//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       return (window.location.href = '/r/session-expired');
//     }
//     return error;
//   }
// }

// async function downloadFile(path, defaultFileName = 'download.xlsx') {
//   try {
//     const response = await service.get(path, { responseType: 'blob' });

//     // Read filename from API headers
//     const contentDisposition = response.headers['content-disposition'];
//     let fileName = defaultFileName;

//     if (contentDisposition && contentDisposition.includes('filename=')) {
//       fileName = contentDisposition.split('filename=')[1].replace(/"/g, '');
//     }

//     // Convert response to downloadable file
//     const blob = new Blob([response.data]);
//     const link = document.createElement('a');
//     link.href = window.URL.createObjectURL(blob);
//     link.download = fileName;
//     link.click();

//     return true;
//   } catch (error) {
//     console.error('Download error:', error);

//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       window.location.href = '/r/session-expired';
//     }

//     return false;
//   }
// }

// async function downloadFilePOST(path, formData, defaultFileName = 'download.xlsx') {
//   try {
//     const response = await service.post(path, formData, {
//       responseType: 'blob', // important for file download
//     });

//     const contentDisposition = response.headers['content-disposition'];
//     let fileName = defaultFileName;

//     if (contentDisposition && contentDisposition.includes('filename=')) {
//       fileName = contentDisposition.split('filename=')[1].replace(/"/g, '');
//     }

//     const blob = new Blob([response.data]);
//     const link = document.createElement('a');
//     link.href = window.URL.createObjectURL(blob);
//     link.download = fileName;
//     link.click();
//     return true;
//   } catch (error) {
//     console.error('Download error:', error);

//     if (error?.response?.status === 403) {
//       localStorage.clear();
//       window.location.href = '/r/session-expired';
//     }

//     return false;
//   }
// }

// export default {
//   get,
//   post,
//   put,
//   patch,
//   remove,
//   uploadFile,
//   downloadFile,
//   downloadFilePOST,
// };
